// Content data for QuByt AI Landing Page

export const heroData = {
  headline: "Businesses lose hundreds of billions annually solving problems from scratch.",
  subheadline:
    "QuByt AI is a single platform that generates complete, secure business applications from a set of specialized AI agents. Choose an industry pack — healthcare, logistics, e-commerce — select the components you need — customer, inventory, payments — and deploy a working web or mobile application without writing code or wiring up integrations.",
  description: "",
  stats: [
    { value: "3", label: "steps from idea to a deployed application" },
    { value: "0", label: "lines of code required" },
    { value: "3", label: "industry packs available at launch" }
  ]
};

export const differentiationData = [
  {
    id: 1,
    title: "Speed",
    subtitle: "From idea to deployed application in minutes",
    metric: "Minutes, not months",
    challenge: "Traditional development cycles typically run 6–12 months from spec to launch.",
    breakthrough:
      "Our AI agents design, build, test, and deploy a complete business application end to end, without a development team in the loop.",
    impact: "Ship a working product while a traditional build is still in planning.",
    visual: "speed"
  },
  {
    id: 2,
    title: "Agent Intelligence",
    subtitle: "Agents that reason about your business, not just templates",
    metric: "Context-aware automation",
    challenge: "Many \"AI-powered\" tools are template generators with a chat interface bolted on.",
    breakthrough:
      "QuByt agents understand the domain they're deployed in, make configuration decisions, and adapt the application as your requirements change.",
    impact: "Less manual reconfiguration as your business evolves.",
    visual: "intelligence"
  },
  {
    id: 3,
    title: "Native Integration",
    subtitle: "Components are designed to work together from the start",
    metric: "No integration backlog",
    challenge: "Enterprise integration work commonly consumes a large share of a project's development time.",
    breakthrough:
      "Every component is built on a shared, agent-native architecture, so customer, inventory, and payment modules interoperate without custom middleware.",
    impact: "Fewer integration defects and less ongoing maintenance overhead.",
    visual: "integration"
  },
  {
    id: 4,
    title: "Security by Default",
    subtitle: "Protection built into the foundation, not layered on after",
    metric: "Continuous monitoring",
    challenge: "Security is often addressed reactively, after a vulnerability has already been exposed.",
    breakthrough:
      "Applications are generated with authentication, encryption, and access controls in place from day one, with continuous monitoring for anomalies.",
    impact: "A stronger security baseline without a dedicated security engineering effort.",
    visual: "security"
  },
  {
    id: 5,
    title: "Domain Expertise",
    subtitle: "Industry knowledge built into every agent pack",
    metric: "Pre-configured compliance",
    challenge: "Generic software forces your team to adapt workflows to the tool instead of the other way around.",
    breakthrough:
      "Each industry pack is pre-configured with the regulations, workflows, and terminology relevant to that sector.",
    impact: "Launch a solution that already reflects how your industry operates.",
    visual: "expertise"
  },
  {
    id: 6,
    title: "Ongoing Optimization",
    subtitle: "Applications that get maintained, not just deployed",
    metric: "Automated upkeep",
    challenge: "Software typically degrades over time and requires dedicated maintenance resources.",
    breakthrough:
      "Deployed applications are monitored continuously, with agents applying performance and reliability improvements over time.",
    impact: "Lower ongoing maintenance burden for your team.",
    visual: "optimization"
  }
];

export const featuresData = {
  title: "The Complete Platform",
  subtitle:
    "Everything needed to go from idea to a secure, production-ready application — generated by AI agents that understand both your industry and how the pieces fit together.",
  pillars: [
    {
      title: "Domain-Specific Agent Packs",
      subtitle: "Healthcare, Logistics, E-commerce",
      description:
        "Pre-configured AI agents with domain knowledge built in. From patient management workflows aligned to HIPAA, to supply chain optimization, to payment processing — each pack reflects the regulations and practices specific to that sector.",
      features: [
        "Healthcare: patient records, appointment scheduling, insurance claims",
        "Logistics: inventory tracking, route optimization, warehouse management",
        "E-commerce: product catalogs, order processing, customer service",
        "Financial: compliance monitoring, risk assessment, transaction processing"
      ],
      icon: "Building"
    },
    {
      title: "Business Components",
      subtitle: "Customer, Inventory, Payments",
      description:
        "The building blocks most businesses need. These components are designed to understand how they relate to each other, so data flows and operational logic stay consistent across your application.",
      features: [
        "Customer Management: unified profiles with usage and engagement history",
        "Inventory Control: automated restocking based on demand signals",
        "Payment Processing: multi-gateway support with fraud detection",
        "Analytics: real-time reporting across connected components"
      ],
      icon: "Layers"
    },
    {
      title: "Security Built In",
      subtitle: "Protection from the first line of code",
      description:
        "Security controls are part of the generation process, not an add-on step. Applications are monitored continuously and evaluated against relevant compliance requirements as they run.",
      features: [
        "Continuous threat monitoring across deployed applications",
        "Compliance frameworks for SOC 2, HIPAA, and PCI-DSS built into the foundation",
        "Zero-trust access controls with encryption in transit and at rest",
        "Automated patching for known vulnerabilities"
      ],
      icon: "Shield"
    }
  ]
};

export const trustedByData = {
  title: "Built For Regulated, High-Stakes Industries",
  companies: [
    "Financial Services",
    "Healthcare",
    "Logistics & Supply Chain",
    "E-commerce",
    "Enterprise Software"
  ]
};

export const aboutData = {
  title: "Building the Infrastructure for AI-Generated Business Software",
  subtitle: "A platform for turning business requirements into secure, working applications",
  mission: {
    title: "Our Mission",
    description:
      "To make it possible for any organization to go from a business requirement to a secure, compliant, production-ready application in minutes rather than months — without requiring a development team for every project."
  },
  story: {
    title: "Our Story",
    description:
      "We spent years building and shipping enterprise software and kept running into the same pattern: promising AI prototypes that stalled at the last mile on compliance, integration, and security. Off-the-shelf platforms offered rigid templates that didn't reflect how specific industries actually operate, and every integration required custom engineering work. We built QuByt AI to close that gap — a platform where every component is designed to work with the others from the start, and where domain knowledge and security are part of the generation process rather than something layered on afterward."
  },
  whatWeBuild: {
    title: "What We Built",
    description:
      "A platform that takes a business requirement and produces a working, compliant application — web or mobile — using AI agents trained on the specifics of healthcare, logistics, and e-commerce workflows. The agents don't just generate code; they apply the compliance and integration logic relevant to the domain they're working in."
  },
  impact: {
    title: "Where It's Used Today",
    description:
      "Teams use QuByt AI to stand up supply-chain and commerce workflows, patient intake experiences, customer portals, and back-office automation — with security and domain-specific logic built in from the start."
  },
  difference: {
    title: "What Sets Us Apart",
    description:
      "Most platforms ask you to choose between moving fast and building securely. QuByt AI is designed so you don't have to make that trade-off — compliance and integration are handled as part of generation, not as a follow-up project."
  },
  vision: {
    title: "Our Vision",
    description:
      "A future where any business requirement can become secure, working software in minutes — where AI agents handle the implementation complexity so teams can focus on the problem they're actually trying to solve."
  },
  tagline: "QuByt AI — From Business Requirement to Secure Application",
  values: [
    {
      title: "Reliable AI Systems",
      description:
        "We build agents whose behavior is predictable and explainable, so teams can trust what gets generated and deployed on their behalf."
    },
    {
      title: "Compliance-First Approach",
      description:
        "Security, governance, and auditability are part of the generation process — not a separate step after the application is built."
    },
    {
      title: "Fast Time-to-Production",
      description:
        "Move from requirement to a working, deployable application quickly, without cutting corners on quality or compliance."
    },
    {
      title: "No Compromise on Fundamentals",
      description:
        "Speed doesn't come at the cost of security or governance. Both are treated as requirements, not trade-offs."
    }
  ],
  team: {
    title: "Leadership Team",
    subtitle: "Experienced founders with a track record of building enterprise and AI platforms",
    members: [
      {
        name: "Krish Chaitanya Munirathnam",
        role: "CEO & Co-Founder",
        credentials: "MBA, NYU Stern",
        background: "20+ years in enterprise software"
      },
      {
        name: "Dhiraj Kafle",
        role: "COO & Co-Founder",
        credentials: "B.S., University of Illinois",
        background: "25+ years in product and enterprise software"
      },
      {
        name: "Sudheer Kandukuru",
        role: "Chief AI Officer",
        credentials: "M.S., Texas A&M International University",
        background: "12+ years building enterprise applications at scale"
      },
      {
        name: "Kunwar Bhattacharya",
        role: "Sr. Manager, Product & UI/UX",
        credentials: "MBA, NYU Stern",
        background: "15+ years in digital product"
      }
    ]
  }
};

export const ctaData = {
  primary: {
    title: "See QuByt AI on Your Use Case",
    description: "Talk to our team about deploying a secure, compliant application for your business.",
    buttonText: "Start Free Trial",
    secondaryText: "Book a Demo"
  },
  secondary: {
    title: "Ready to Get Started?",
    description: "Set up your first QuByt AI application in minutes.",
    buttonText: "Get Started Now",
    features: ["30-day free trial", "No setup fees", "24/7 support"]
  }
};

export const navigationData = {
  logo: "QuByt AI",
  links: [
    { name: "Platform", href: "#features" },
    { name: "Industries", href: "#industries" },
    { name: "Security", href: "#security" },
    { name: "About", href: "#about" }
  ],
  cta: "Get Started"
};
